import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { User, Session } from "@supabase/supabase-js";
import { clearLocalSession, getSessionRemovalError, isCurrentSession, isSessionTerminated, onLocalSignOut, supabase } from "./supabaseClient";
import { store } from "../store/store";
import { pedagogueApi, postgresApi } from "../store/api";

const clearAccountQueries = () => {
  store.dispatch(postgresApi.util.resetApiState());
  store.dispatch(pedagogueApi.util.resetApiState());
};

const currentLoginUrl = () => {
  const appHome = process.env.REACT_APP_APP_HOME_URL || "https://app.pedagogue.io";
  const returnTo = `${window.location.origin}${window.location.pathname}${window.location.search}`;
  const login = new URL(`${appHome}/login`);
  login.searchParams.set("return_to", returnTo);
  login.searchParams.set("signed_out", "1");
  return login.href;
};

/**
 * G2 terminal-auth session validation state.
 * - "pending": a local session exists but the backend has not confirmed it yet.
 * - "ok": the backend accepted the session (or the failure was transient).
 * - "terminated": the backend actively rejected the session (revoked session,
 *   blocked account, or G2 `access_locked`) and the local session was cleared.
 */
export type SessionValidationState = "pending" | "ok" | "terminated";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  sessionValidation: SessionValidationState;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionValidation, setSessionValidation] =
    useState<SessionValidationState>(() => isSessionTerminated() ? "terminated" : "pending");
  const [loginUrl, setLoginUrl] = useState(currentLoginUrl);
  // Supabase can emit more than one Session object for the same credential
  // while restoring local storage. Track the credential rather than an object
  // identity so that a duplicate event cannot cancel its own validation.
  const validationTokenRef = useRef<string | null>(null);

  // G2 terminal-auth handling: a locally-stored Supabase session is not proof
  // the account is still active — cancellation/offboarding deactivates users
  // and revokes sessions server-side. Validate the session once against the
  // backend before the route guard renders any authenticated UI.
  //
  // Contract (structured-code-first):
  // - 403 `trial_expired`   → untouched here; the global trialExpiredHandler
  //   fetch interceptor (installed in App.tsx) redirects to /trial-expired
  //   and the session stays in place.
  // - 403 `access_locked`   → clear the local session; route guard sends the
  //   user to the app-web login surface.
  // - other 401/generic 403 → same as access_locked.
  // - network errors / 5xx  → transient; NEVER clear the session for these.
  const validationAccessToken = session?.access_token ?? null;
  useEffect(() => {
    if (isSessionTerminated()) { return; }
    if (!validationAccessToken) {
      validationTokenRef.current = null;
      // Keep "terminated" sticky after the session is cleared so the route
      // guard redirects to login instead of re-attempting auto-handoff with
      // credentials the backend just rejected.
      setSessionValidation((current) =>
        current === "terminated" ? current : "pending"
      );
      return;
    }
    if (validationTokenRef.current === validationAccessToken) return;
    validationTokenRef.current = validationAccessToken;

    let cancelled = false;

    const validate = async () => {
      const apiBaseUrl = process.env.REACT_APP_BACKEND_BASE_URL;
      if (!apiBaseUrl) {
        setSessionValidation("ok");
        return;
      }
      setSessionValidation("pending");
      try {
        const response = await fetch(`${apiBaseUrl}/users/me`, {
          headers: { Authorization: `Bearer ${validationAccessToken}` },
        });
        if (cancelled || isSessionTerminated()) { return; }
        if (response.status === 401 || response.status === 403) {
          let errorCode: string | undefined;
          try {
            const body = (await response.clone().json()) as {
              error_code?: string;
            };
            errorCode = body?.error_code;
          } catch {
            // Non-JSON body — treat as a generic terminal auth failure.
          }
          if (cancelled || isSessionTerminated()) { return; }
          if (errorCode === "trial_expired") {
            // Existing contract, unchanged: the global fetch interceptor has
            // already redirected to /trial-expired. Keep the session.
            setSessionValidation("ok");
            return;
          }
          const token = clearLocalSession(validationAccessToken);
          // Remote revocation never owns local state or blocks the signed-out UI.
          if (token) { void supabase.auth.admin.signOut(token, "local").catch(() => undefined); }
          return;
        }
        // 2xx and transient failures (5xx) both keep the session — only an
        // explicit auth rejection may destroy local credentials.
        setSessionValidation("ok");
      } catch {
        // Network error — transient; never sign out on connectivity issues.
        if (!cancelled && !isSessionTerminated()) { setSessionValidation("ok"); }
      }
    };

    validate();
    return () => {
      cancelled = true;
    };
  }, [validationAccessToken]);

  useEffect(() => {
    let mounted = true;
    const signedOut = () => {
      clearAccountQueries();
      if (!mounted) { return; }
      setLoginUrl(currentLoginUrl());
      setUser(null);
      setSession(null);
      setLoading(false);
      setSessionValidation("terminated");
    };
    const unsubscribeLocal = onLocalSignOut(signedOut);
    if (isSessionTerminated()) { signedOut(); }

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted || isSessionTerminated()) { return; }
      if (!isCurrentSession(session)) { setLoading(false); return; }
      if (event === "SIGNED_OUT") {
        clearLocalSession();
        return;
      }
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => {
      mounted = false;
      unsubscribeLocal();
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (sessionValidation === "terminated" && !getSessionRemovalError()) {
      window.location.href = loginUrl;
    }
  }, [sessionValidation, loginUrl]);

  const signOut = async () => {
    const token = clearLocalSession(session?.access_token);
    const apiBaseUrl = process.env.REACT_APP_BACKEND_BASE_URL;
    // Attach handlers to both requests immediately, even while one is held.
    const remote: Promise<unknown>[] = [];
    if (token) {
      remote.push(supabase.auth.admin.signOut(token, "global"));
      if (apiBaseUrl) {
        remote.push(fetch(`${apiBaseUrl}/auth/signout`, {
          method: "POST",
          keepalive: true,
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        }));
      }
    }
    await Promise.allSettled(remote);
  };

  const value = {
    user,
    session,
    loading,
    sessionValidation,
    signOut,
  };

  return (
    <AuthContext.Provider value={value}>
      {sessionValidation === "terminated" ? (
        <main className="min-h-screen flex items-center justify-center p-6">
          <div role="status">
            <h1>{getSessionRemovalError() ? "Sign-out could not be completed." : "You have been signed out."}</h1>
            {getSessionRemovalError() ? <p>{getSessionRemovalError()?.message}</p> : <a href={loginUrl}>Sign in</a>}
          </div>
        </main>
      ) : children}
    </AuthContext.Provider>
  );
};
